import React, { useContext, useState, useCallback, useEffect, useMemo } from 'react'

import moment from 'moment'
import { isString, isNull } from 'lodash'
import { useSelector } from 'react-redux'
import { faClinicMedical, faStethoscope, faUserGraduate } from '@fortawesome/pro-solid-svg-icons'
import { faCalendar } from '@fortawesome/pro-regular-svg-icons'

import { DATE_WITHOUT_TIME, Contexts } from '../../utils/constants'
import { getMaccsInternshipMessages, sendMaccsInternshipMessage } from '../../utils/api/internshipMessage'
import { displayError } from '../../utils/apiHelper'
import { isObserver } from '../../utils/roles'
import { isObject } from '../../utils'
import IconLabel, { FontSizes } from '../icons/IconLabel'
import { ColorsPalette } from '../../config/colors'
import { GlobalFiltersContext } from '../../Providers/GlobalFiltersProvider'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { Routes } from '../../routes'
import { selectTranslate } from '../../reducers/LocaleReducer/LocalReducer'
import { selectUser } from '../../reducers/UserReducer'
import ChatWidget, { MESSAGE_RECEIVED, MESSAGE_SENT } from '../shared/ChatWidget'

const MaccsChatManager = ({ internship, onClose }) => {
  const { setInternshipDate, setInternshipSearch } = useContext(GlobalFiltersContext)

  const history = useHistory()
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [messages, setMessages] = useState([])

  const messageToBeUnread = useMemo(() => {
    return messages.length > 0 && messages[messages.length - 1].origin && messages[messages.length - 1].origin !== MESSAGE_SENT ? messages[messages.length - 1] : null
  }, [messages, user.id])

  const studentName = useMemo(() => {
    let name = ''

    if (internship && isObject(internship.assistant)) {
      name = `${internship.assistant.lastname} ${internship.assistant.firstname}`
    }

    return name
  }, [internship])

  const fetchMessages = useCallback(() => {
    if (internship) {
      getMaccsInternshipMessages(user, internship).then(json => {
        if (json.data) {
          const lastItem = messages.length === 0 ? null : messages[messages.length - 1]
          const lastNewItem = json.data.length === 0 ? null : json.data[json.data.length - 1]

          if (
            (isNull(lastItem) && !isNull(lastNewItem)) ||
            (!isNull(lastItem) && isNull(lastNewItem)) ||
            (!isNull(lastItem) && !isNull(lastNewItem) && lastItem.id !== lastNewItem.id)
          ) {
            setMessages(json.data.map(m => {
              m.origin = user.username === m.from.username ? MESSAGE_SENT : MESSAGE_RECEIVED

              return m
            }))
          }
        }
      })
    }
  }, [user.id, internship?.id, messages, setMessages])

  useEffect(() => {
    fetchMessages()
    const interval = setInterval(fetchMessages, 5000)

    if (internship === null) {
      clearInterval(interval)
    }

    return () => clearInterval(interval)
  }, [fetchMessages, internship])

  const postMessage = message => {
    sendMaccsInternshipMessage(user, { maccsInternship: internship.id, content: message }).then(json => {
      const message = json.data

      message.from = { username: user.username }

      setMessages([...messages, message])
    }).catch(error => {
      displayError(error, 'internship_message.send')
    })
  }

  const getDate = date => {
    if (isString(date)) {
      if (date.includes('/')) {
        return date
      }

      if (date.includes('+')) {
        date = date.split('+')[0]
      }
    }

    const formatedDate = moment(date).format('DD/MM/YY')

    if (formatedDate === 'Invalid date') {
      return date
    }

    return formatedDate
  }

  const handleNavigate = useCallback(() => {
    history.push(Routes.INTERNSHIPS)
  }, [history.location])

  const handleCalendarChange = () => {
    let date = moment().format(DATE_WITHOUT_TIME)

    if (internship.startDate.includes('T')) {
      const dateParts = internship.startDate.split('T')
      date = moment(dateParts[0])
    } else if (internship.startDate.includes('/')) {
      const dateParts = internship.startDate.split('/')
      date = moment(`20${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`)
    }

    setInternshipSearch(studentName)
    setInternshipDate(date)
    handleNavigate()
  }

  const widgetTitle = useMemo(() => {
    let name = t('Without assistant')
    let specialityName = t('Without sector')

    if (studentName) {
      name = studentName
    }

    if (isObject(internship) && isObject(internship.speciality)) {
      specialityName = internship.speciality.name
    }

    return (
      <div className='flex-column'>
        <IconLabel icon={faUserGraduate} text={name} color={ColorsPalette.WHITE} fontSize={FontSizes.SMALL} padding={null} />
        <IconLabel icon={faStethoscope} text={specialityName} color={ColorsPalette.WHITE} fontSize={FontSizes.SMALL} padding={null} />
      </div>
    )
  }, [internship, studentName, t])

  const widgetSubtitle = useMemo(() => {
    const isSchoolContext = user.context === Contexts.SCHOOL

    let period = t('Without period')
    let establishmentName = t('Without establishment')

    if (internship) {
      period = `${getDate(internship?.startDate)} - ${getDate(internship?.endDate)}`

      if (isSchoolContext) {
        establishmentName = isObject(internship.institution) ? `${t('Contact')} ${internship.institution.name}` : t('Without institution')
      } else {
        establishmentName = isObject(internship.school) ? `${t('Contact')} ${internship.school.name}` : t('Without school')
      }
    }

    return (
      <div className='flex-column'>
        <IconLabel icon={faClinicMedical} text={establishmentName} color={ColorsPalette.WHITE} fontSize={FontSizes.SMALL} padding={null} />
        <IconLabel icon={faCalendar} text={period} color={ColorsPalette.WHITE} fontSize={FontSizes.SMALL} padding={null} />
      </div>
    )
  }, [user.context, internship, t])

  return (
    <>
      {internship && (
        <ChatWidget
          hasUnreadMessages={!!messageToBeUnread}
          messages={messages}
          readOnly={isObserver(user)}
          startDate={internship.startDate}
          subtitle={widgetSubtitle}
          title={widgetTitle}
          onClose={() => onClose(null)}
          onFind={handleCalendarChange}
          onSendMessage={postMessage}
          onUnread={() => onClose(messageToBeUnread)}
        />
      )}
    </>
  )
}

export default MaccsChatManager
